import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import {
    Alert,
    Box,
    Button,
    Chip,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Tab,
    Tabs,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    alpha,
    CircularProgress,
    FormControlLabel,
    Checkbox,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import SwapHorizOutlinedIcon from "@mui/icons-material/SwapHorizOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import SearchIcon from "@mui/icons-material/Search";
import { buildApiUrl, formatApiUnreachableMessage, isSupabaseFunctionsApi } from "../../../config/api";
import { getSchoolIdForUser } from "../../../utils/userContext";
import Popup from "../../../components/Popup";
import ConfirmDeleteDialog from "../../../components/ConfirmDeleteDialog";

const CATEGORIES = ["Book", "TLM", "Reference", "Other"];
const TLM_TYPES = ["Chart", "Model", "Equipment", "Manipulative", "Audio-Visual", "Other"];
const BORROWER_TYPES = ["Student", "Teacher"];
const BORROW_STATUSES = ["all", "Active", "Overdue", "Returned"];

const EMPTY_ITEM = {
    title: "",
    author: "",
    isbn: "",
    category: "Book",
    tlmType: "",
    shelfLocation: "",
    description: "",
    quantityTotal: "1",
    quantityAvailable: "",
    lowStockThreshold: "2",
};

function Kpi({ label, value, sub, alert: isAlert }) {
    return (
        <Paper
            elevation={0}
            sx={{
                p: 2,
                borderRadius: 2,
                border: (t) =>
                    `1px solid ${isAlert ? t.palette.warning.main : t.palette.divider}`,
                height: "100%",
                bgcolor: isAlert ? (t) => alpha(t.palette.warning.main, 0.06) : undefined,
            }}
        >
            <Typography variant="overline" color="text.secondary">
                {label}
            </Typography>
            <Typography variant="h4" fontWeight={800} color={isAlert ? "warning.main" : undefined}>
                {value}
            </Typography>
            {sub ? (
                <Typography variant="caption" color="text.secondary">
                    {sub}
                </Typography>
            ) : null}
        </Paper>
    );
}

function statusChip(status) {
    if (status === "Overdue") return <Chip size="small" color="error" label="Overdue" />;
    if (status === "Returned") return <Chip size="small" color="success" label="Returned" />;
    return <Chip size="small" color="primary" label="Active" />;
}

function fmtDate(d) {
    if (!d) return "—";
    try {
        return new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
    } catch {
        return "—";
    }
}

/**
 * Library & TLMs inventory — admin (full CRUD) or teacher (borrow/return + view).
 */
const LibraryManagement = ({ canManageInventory = true }) => {
    const { currentUser } = useSelector((s) => s.user);
    const schoolId = getSchoolIdForUser(currentUser);
    const teacherId = currentUser?._id || currentUser?.id;

    const [tab, setTab] = useState(0);
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState("");
    const [showPopup, setShowPopup] = useState(false);
    const [popupSeverity, setPopupSeverity] = useState(undefined);

    const [overview, setOverview] = useState(null);
    const [items, setItems] = useState([]);
    const [borrowings, setBorrowings] = useState([]);
    const [report, setReport] = useState(null);

    const [search, setSearch] = useState("");
    const [categoryFilter, setCategoryFilter] = useState("");
    const [lowStockOnly, setLowStockOnly] = useState(false);
    const [borrowStatus, setBorrowStatus] = useState("Active");
    const [borrowSearch, setBorrowSearch] = useState("");
    const [termFilterOnly, setTermFilterOnly] = useState(true);

    const [itemDialog, setItemDialog] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [itemForm, setItemForm] = useState(EMPTY_ITEM);
    const [deleteTarget, setDeleteTarget] = useState(null);

    const [borrowDialog, setBorrowDialog] = useState(false);
    const [borrowForm, setBorrowForm] = useState({
        itemId: "",
        borrowerType: "Student",
        studentId: "",
        teacherId: "",
        dueDate: "",
        notes: "",
    });
    const [borrowers, setBorrowers] = useState({ students: [], teachers: [] });
    const [borrowerQuery, setBorrowerQuery] = useState("");
    const [saving, setSaving] = useState(false);

    const defaultDueDate = useMemo(() => {
        const d = new Date();
        d.setDate(d.getDate() + 14);
        return d.toISOString().slice(0, 10);
    }, []);

    const notify = (msg, severity) => {
        setMessage(msg);
        setPopupSeverity(severity);
        setShowPopup(true);
    };

    const loadOverview = useCallback(async () => {
        if (!schoolId) return;
        const { data } = await axios.get(buildApiUrl(`/LibraryOverview/${schoolId}`));
        setOverview(data);
    }, [schoolId]);

    const loadItems = useCallback(async () => {
        if (!schoolId) return;
        const params = new URLSearchParams();
        if (search.trim()) params.set("q", search.trim());
        if (categoryFilter) params.set("category", categoryFilter);
        if (lowStockOnly) params.set("lowStock", "true");
        const qs = params.toString();
        const { data } = await axios.get(buildApiUrl(`/LibraryItems/${schoolId}${qs ? `?${qs}` : ""}`));
        setItems(Array.isArray(data) ? data : []);
    }, [schoolId, search, categoryFilter, lowStockOnly]);

    const loadBorrowings = useCallback(async () => {
        if (!schoolId) return;
        const params = new URLSearchParams();
        if (borrowStatus) params.set("status", borrowStatus);
        if (borrowSearch.trim()) params.set("q", borrowSearch.trim());
        if (termFilterOnly) params.set("currentTerm", "true");
        const qs = params.toString();
        const { data } = await axios.get(buildApiUrl(`/LibraryBorrowings/${schoolId}${qs ? `?${qs}` : ""}`));
        setBorrowings(Array.isArray(data) ? data : []);
    }, [schoolId, borrowStatus, borrowSearch, termFilterOnly]);

    const loadReport = useCallback(async () => {
        if (!schoolId) return;
        const { data } = await axios.get(buildApiUrl(`/LibraryReport/${schoolId}`));
        setReport(data);
    }, [schoolId]);

    const refreshAll = useCallback(async () => {
        if (!schoolId) return;
        setLoading(true);
        try {
            await Promise.all([loadOverview(), loadItems(), loadBorrowings(), loadReport()]);
        } catch (err) {
            notify(err.response?.data?.message || err.message || "Could not load library data", "error");
        } finally {
            setLoading(false);
        }
    }, [schoolId, loadOverview, loadItems, loadBorrowings, loadReport]);

    useEffect(() => {
        refreshAll();
    }, [refreshAll]);

    useEffect(() => {
        if (!schoolId || tab !== 0) return;
        loadItems();
    }, [schoolId, tab, loadItems]);

    useEffect(() => {
        if (!schoolId || tab !== 1) return;
        loadBorrowings();
    }, [schoolId, tab, loadBorrowings]);

    useEffect(() => {
        if (!schoolId || tab !== 2) return;
        loadReport();
    }, [schoolId, tab, loadReport]);

    const openAddItem = () => {
        setEditingItem(null);
        setItemForm(EMPTY_ITEM);
        setItemDialog(true);
    };

    const openEditItem = (row) => {
        setEditingItem(row);
        setItemForm({
            title: row.title || "",
            author: row.author || "",
            isbn: row.isbn || "",
            category: row.category || "Book",
            tlmType: row.tlmType || "",
            shelfLocation: row.shelfLocation || "",
            description: row.description || "",
            quantityTotal: String(row.quantityTotal ?? 1),
            quantityAvailable: String(row.quantityAvailable ?? ""),
            lowStockThreshold: String(row.lowStockThreshold ?? 2),
        });
        setItemDialog(true);
    };

    const saveItem = async () => {
        if (!itemForm.title.trim()) {
            notify("Title is required", "error");
            return;
        }
        setSaving(true);
        try {
            const payload = {
                schoolId,
                title: itemForm.title.trim(),
                author: itemForm.author.trim(),
                isbn: itemForm.isbn.trim(),
                category: itemForm.category,
                tlmType: itemForm.tlmType,
                shelfLocation: itemForm.shelfLocation.trim(),
                description: itemForm.description.trim(),
                quantityTotal: Number(itemForm.quantityTotal) || 1,
                lowStockThreshold: Number(itemForm.lowStockThreshold) || 2,
            };
            if (itemForm.quantityAvailable !== "") {
                payload.quantityAvailable = Number(itemForm.quantityAvailable);
            }
            if (editingItem) {
                const id = editingItem._id || editingItem.id;
                await axios.put(buildApiUrl(`/LibraryItem/${id}`), payload);
                notify("Item updated", "success");
            } else {
                await axios.post(buildApiUrl("/LibraryItem"), payload);
                notify("Item added to inventory", "success");
            }
            setItemDialog(false);
            await refreshAll();
        } catch (err) {
            const apiMsg = err.response?.data?.message || err.response?.data?.error;
            let msg = apiMsg || (err.code === "ERR_NETWORK" ? formatApiUnreachableMessage() : null);
            if (!msg && err.response?.status === 404 && isSupabaseFunctionsApi()) {
                msg = "Library API is not deployed. Run: supabase functions deploy Library";
            }
            notify(msg || err.message || "Could not save item", "error");
        } finally {
            setSaving(false);
        }
    };

    const confirmDeleteItem = async () => {
        if (!deleteTarget) return;
        const id = deleteTarget._id || deleteTarget.id;
        try {
            await axios.delete(buildApiUrl(`/LibraryItem/${id}`));
            notify("Item removed", "success");
            setDeleteTarget(null);
            await refreshAll();
        } catch (err) {
            notify(err.response?.data?.message || "Could not delete item", "error");
            setDeleteTarget(null);
        }
    };

    const openBorrowDialog = (itemId = "") => {
        setBorrowForm({
            itemId: itemId || "",
            borrowerType: "Student",
            studentId: "",
            teacherId: canManageInventory ? "" : teacherId || "",
            dueDate: defaultDueDate,
            notes: "",
        });
        setBorrowerQuery("");
        setBorrowDialog(true);
    };

    const loadBorrowers = useCallback(async (q) => {
        if (!schoolId) return;
        try {
            const params = q ? `?q=${encodeURIComponent(q)}` : "";
            const { data } = await axios.get(buildApiUrl(`/LibraryBorrowers/${schoolId}${params}`));
            setBorrowers({
                students: data?.students || [],
                teachers: data?.teachers || [],
            });
        } catch {
            setBorrowers({ students: [], teachers: [] });
        }
    }, [schoolId]);

    useEffect(() => {
        if (!borrowDialog) return;
        const t = setTimeout(() => loadBorrowers(borrowerQuery), 250);
        return () => clearTimeout(t);
    }, [borrowDialog, borrowerQuery, loadBorrowers]);

    const saveBorrow = async () => {
        if (!borrowForm.itemId) {
            notify("Select an item to borrow", "error");
            return;
        }
        if (borrowForm.borrowerType === "Student" && !borrowForm.studentId) {
            notify("Select a student", "error");
            return;
        }
        if (borrowForm.borrowerType === "Teacher" && !borrowForm.teacherId) {
            notify("Select a teacher", "error");
            return;
        }
        setSaving(true);
        try {
            await axios.post(buildApiUrl("/LibraryBorrowing"), {
                schoolId,
                itemId: borrowForm.itemId,
                borrowerType: borrowForm.borrowerType,
                studentId: borrowForm.borrowerType === "Student" ? borrowForm.studentId : undefined,
                teacherId: borrowForm.borrowerType === "Teacher" ? borrowForm.teacherId : undefined,
                dueDate: borrowForm.dueDate || defaultDueDate,
                notes: borrowForm.notes,
                recordedByTeacherId: teacherId,
            });
            notify("Borrowing recorded", "success");
            setBorrowDialog(false);
            await refreshAll();
        } catch (err) {
            notify(err.response?.data?.message || "Could not record borrowing", "error");
        } finally {
            setSaving(false);
        }
    };

    const returnItem = async (row) => {
        const id = row._id || row.id;
        try {
            await axios.put(buildApiUrl(`/LibraryBorrowing/${id}/return`));
            notify("Return recorded", "success");
            await refreshAll();
        } catch (err) {
            notify(err.response?.data?.message || "Could not record return", "error");
        }
    };

    const availableItems = useMemo(
        () => items.filter((i) => Number(i.quantityAvailable) > 0),
        [items],
    );

    return (
        <Box
            sx={{
                minHeight: "100%",
                bgcolor: (t) => alpha(t.palette.primary.main, 0.03),
                pb: 4,
            }}
        >
            <Container maxWidth="lg" sx={{ pt: 3 }}>
                <Paper
                    elevation={0}
                    sx={{
                        p: { xs: 2.5, md: 3 },
                        mb: 3,
                        borderRadius: 3,
                        background: (t) =>
                            `linear-gradient(135deg, ${alpha(t.palette.primary.main, 0.12)} 0%, ${alpha(t.palette.secondary.main, 0.08)} 100%)`,
                        border: (t) => `1px solid ${alpha(t.palette.primary.main, 0.15)}`,
                    }}
                >
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }} justifyContent="space-between">
                        <Stack direction="row" spacing={2} alignItems="center">
                            <MenuBookOutlinedIcon sx={{ fontSize: 40, color: "primary.main" }} />
                            <Box>
                                <Typography variant="h5" fontWeight={800}>
                                    Library & TLMs
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Books, teaching materials, borrowing register, and stock reports
                                </Typography>
                            </Box>
                        </Stack>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            <Button variant="outlined" startIcon={<SwapHorizOutlinedIcon />} onClick={() => openBorrowDialog()}>
                                Record borrowing
                            </Button>
                            {canManageInventory ? (
                                <Button variant="contained" startIcon={<AddIcon />} onClick={openAddItem}>
                                    Add item
                                </Button>
                            ) : null}
                        </Stack>
                    </Stack>
                </Paper>

                {overview?.lowStockCount > 0 ? (
                    <Alert severity="warning" icon={<WarningAmberOutlinedIcon />} sx={{ mb: 2, borderRadius: 2 }}>
                        {overview.lowStockCount} item{overview.lowStockCount === 1 ? "" : "s"} at or below low-stock threshold.
                        {canManageInventory ? " Review inventory and reorder." : " Inform the library officer."}
                    </Alert>
                ) : null}

                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={6} sm={3}>
                        <Kpi label="Titles" value={overview?.itemCount ?? "—"} sub="Unique resources" />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <Kpi label="Copies available" value={overview?.availableCopies ?? "—"} sub={`${overview?.onLoan ?? 0} on loan`} />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <Kpi
                            label="Overdue"
                            value={overview?.overdueLoans ?? "—"}
                            sub="Active loans past due"
                            alert={Number(overview?.overdueLoans) > 0}
                        />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <Kpi
                            label="Low stock"
                            value={overview?.lowStockCount ?? "—"}
                            sub="Need restocking"
                            alert={Number(overview?.lowStockCount) > 0}
                        />
                    </Grid>
                </Grid>

                <Paper elevation={0} sx={{ borderRadius: 2, border: (t) => `1px solid ${t.palette.divider}` }}>
                    <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 2, borderBottom: 1, borderColor: "divider" }}>
                        <Tab label="Inventory" icon={<MenuBookOutlinedIcon fontSize="small" />} iconPosition="start" />
                        <Tab label="Borrowings" icon={<SwapHorizOutlinedIcon fontSize="small" />} iconPosition="start" />
                        <Tab label="Reports" icon={<AssessmentOutlinedIcon fontSize="small" />} iconPosition="start" />
                    </Tabs>

                    {loading && tab === 0 ? (
                        <Box sx={{ py: 6, textAlign: "center" }}>
                            <CircularProgress size={32} />
                        </Box>
                    ) : null}

                    {tab === 0 && !loading ? (
                        <Box sx={{ p: 2 }}>
                            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
                                <TextField
                                    size="small"
                                    placeholder="Search title, author, ISBN, shelf…"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: "text.secondary" }} /> }}
                                    sx={{ minWidth: 220, flex: 1 }}
                                />
                                <FormControl size="small" sx={{ minWidth: 140 }}>
                                    <InputLabel>Category</InputLabel>
                                    <Select label="Category" value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
                                        <MenuItem value="">All</MenuItem>
                                        {CATEGORIES.map((c) => (
                                            <MenuItem key={c} value={c}>{c}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <Button
                                    size="small"
                                    variant={lowStockOnly ? "contained" : "outlined"}
                                    color="warning"
                                    onClick={() => setLowStockOnly((v) => !v)}
                                >
                                    Low stock only
                                </Button>
                            </Stack>

                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Title</TableCell>
                                            <TableCell>Category</TableCell>
                                            <TableCell>Author / Type</TableCell>
                                            <TableCell>Shelf</TableCell>
                                            <TableCell align="right">Avail / Total</TableCell>
                                            <TableCell align="right">Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {items.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={6} align="center" sx={{ py: 4, color: "text.secondary" }}>
                                                    No items found. {canManageInventory ? "Add your first book or TLM." : ""}
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            items.map((row) => {
                                                const id = row._id || row.id;
                                                return (
                                                    <TableRow key={id} hover>
                                                        <TableCell sx={{ fontWeight: 600 }}>
                                                            {row.title}
                                                            {row.isLowStock ? (
                                                                <Chip size="small" color="warning" label="Low" sx={{ ml: 1 }} />
                                                            ) : null}
                                                        </TableCell>
                                                        <TableCell>{row.category}</TableCell>
                                                        <TableCell>
                                                            {row.category === "TLM" && row.tlmType
                                                                ? row.tlmType
                                                                : row.author || "—"}
                                                        </TableCell>
                                                        <TableCell>{row.shelfLocation || "—"}</TableCell>
                                                        <TableCell align="right">
                                                            {row.quantityAvailable} / {row.quantityTotal}
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                                                {Number(row.quantityAvailable) > 0 ? (
                                                                    <Button size="small" onClick={() => openBorrowDialog(id)}>
                                                                        Borrow
                                                                    </Button>
                                                                ) : null}
                                                                {canManageInventory ? (
                                                                    <>
                                                                        <Button size="small" onClick={() => openEditItem(row)}>
                                                                            Edit
                                                                        </Button>
                                                                        <Button size="small" color="error" onClick={() => setDeleteTarget(row)}>
                                                                            Delete
                                                                        </Button>
                                                                    </>
                                                                ) : null}
                                                            </Stack>
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    ) : null}

                    {tab === 1 ? (
                        <Box sx={{ p: 2 }}>
                            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mb: 2 }}>
                                <TextField
                                    size="small"
                                    placeholder="Search borrower or title…"
                                    value={borrowSearch}
                                    onChange={(e) => setBorrowSearch(e.target.value)}
                                    sx={{ flex: 1 }}
                                />
                                <FormControl size="small" sx={{ minWidth: 140 }}>
                                    <InputLabel>Status</InputLabel>
                                    <Select label="Status" value={borrowStatus} onChange={(e) => setBorrowStatus(e.target.value)}>
                                        {BORROW_STATUSES.map((s) => (
                                            <MenuItem key={s} value={s}>
                                                {s === "all" ? "All" : s}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={termFilterOnly}
                                            onChange={(e) => setTermFilterOnly(e.target.checked)}
                                        />
                                    }
                                    label="Current term only"
                                />
                            </Stack>

                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Item</TableCell>
                                            <TableCell>Borrower</TableCell>
                                            <TableCell>Type</TableCell>
                                            <TableCell>Term</TableCell>
                                            <TableCell>Borrowed</TableCell>
                                            <TableCell>Due</TableCell>
                                            <TableCell>Status</TableCell>
                                            <TableCell align="right">Action</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {borrowings.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={8} align="center" sx={{ py: 4, color: "text.secondary" }}>
                                                    No borrowing records match your filters.
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            borrowings.map((row) => {
                                                const id = row._id || row.id;
                                                const canReturn = row.status !== "Returned";
                                                return (
                                                    <TableRow key={id} hover>
                                                        <TableCell sx={{ fontWeight: 600 }}>
                                                            {row.item?.title || "—"}
                                                        </TableCell>
                                                        <TableCell>{row.borrowerName}</TableCell>
                                                        <TableCell>{row.borrowerType}</TableCell>
                                                        <TableCell>
                                                            {[row.term, row.academicYear].filter(Boolean).join(" · ") || "—"}
                                                        </TableCell>
                                                        <TableCell>{fmtDate(row.borrowedAt)}</TableCell>
                                                        <TableCell>{fmtDate(row.dueDate)}</TableCell>
                                                        <TableCell>{statusChip(row.status)}</TableCell>
                                                        <TableCell align="right">
                                                            {canReturn ? (
                                                                <Button size="small" variant="outlined" onClick={() => returnItem(row)}>
                                                                    Return
                                                                </Button>
                                                            ) : (
                                                                "—"
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    ) : null}

                    {tab === 2 && report ? (
                        <Box sx={{ p: 2 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Generated {fmtDate(report.generatedAt)}
                            </Typography>

                            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                                Stock by category
                            </Typography>
                            <TableContainer sx={{ mb: 3 }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Category</TableCell>
                                            <TableCell align="right">Titles</TableCell>
                                            <TableCell align="right">Total copies</TableCell>
                                            <TableCell align="right">Available</TableCell>
                                            <TableCell align="right">On loan</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {(report.categorySummary || []).map((row) => (
                                            <TableRow key={row.category}>
                                                <TableCell>{row.category}</TableCell>
                                                <TableCell align="right">{row.titles}</TableCell>
                                                <TableCell align="right">{row.totalCopies}</TableCell>
                                                <TableCell align="right">{row.available}</TableCell>
                                                <TableCell align="right">{row.onLoan}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                                Low-stock items
                            </Typography>
                            {(report.lowStockItems || []).length === 0 ? (
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                    All items above threshold.
                                </Typography>
                            ) : (
                                <TableContainer sx={{ mb: 3 }}>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Title</TableCell>
                                                <TableCell>Category</TableCell>
                                                <TableCell align="right">Available</TableCell>
                                                <TableCell align="right">Threshold</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {report.lowStockItems.map((row) => (
                                                <TableRow key={row._id || row.id}>
                                                    <TableCell>{row.title}</TableCell>
                                                    <TableCell>{row.category}</TableCell>
                                                    <TableCell align="right">{row.quantityAvailable}</TableCell>
                                                    <TableCell align="right">{row.lowStockThreshold}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}

                            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                                Borrowing summary
                            </Typography>
                            <Stack direction="row" spacing={2} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
                                <Chip label={`Active: ${report.borrowingStats?.active ?? 0}`} color="primary" variant="outlined" />
                                <Chip label={`Overdue: ${report.borrowingStats?.overdue ?? 0}`} color="error" variant="outlined" />
                                <Chip label={`Returned: ${report.borrowingStats?.returned ?? 0}`} color="success" variant="outlined" />
                            </Stack>
                        </Box>
                    ) : null}
                </Paper>
            </Container>

            <Dialog open={itemDialog} onClose={() => setItemDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{editingItem ? "Edit item" : "Add book or TLM"}</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={12}>
                            <TextField fullWidth required label="Title" value={itemForm.title} onChange={(e) => setItemForm({ ...itemForm, title: e.target.value })} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Category</InputLabel>
                                <Select label="Category" value={itemForm.category} onChange={(e) => setItemForm({ ...itemForm, category: e.target.value })}>
                                    {CATEGORIES.map((c) => (
                                        <MenuItem key={c} value={c}>{c}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            {itemForm.category === "TLM" ? (
                                <FormControl fullWidth>
                                    <InputLabel>TLM type</InputLabel>
                                    <Select label="TLM type" value={itemForm.tlmType} onChange={(e) => setItemForm({ ...itemForm, tlmType: e.target.value })}>
                                        <MenuItem value="">—</MenuItem>
                                        {TLM_TYPES.map((t) => (
                                            <MenuItem key={t} value={t}>{t}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            ) : (
                                <TextField fullWidth label="Author" value={itemForm.author} onChange={(e) => setItemForm({ ...itemForm, author: e.target.value })} />
                            )}
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth label="ISBN / catalog code" value={itemForm.isbn} onChange={(e) => setItemForm({ ...itemForm, isbn: e.target.value })} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth label="Shelf location" value={itemForm.shelfLocation} onChange={(e) => setItemForm({ ...itemForm, shelfLocation: e.target.value })} />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField fullWidth type="number" label="Total copies" value={itemForm.quantityTotal} onChange={(e) => setItemForm({ ...itemForm, quantityTotal: e.target.value })} inputProps={{ min: 1 }} />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField fullWidth type="number" label="Available (optional)" value={itemForm.quantityAvailable} onChange={(e) => setItemForm({ ...itemForm, quantityAvailable: e.target.value })} helperText="Leave blank when adding new stock" />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField fullWidth type="number" label="Low-stock alert at" value={itemForm.lowStockThreshold} onChange={(e) => setItemForm({ ...itemForm, lowStockThreshold: e.target.value })} inputProps={{ min: 0 }} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth multiline minRows={2} label="Description" value={itemForm.description} onChange={(e) => setItemForm({ ...itemForm, description: e.target.value })} />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setItemDialog(false)}>Cancel</Button>
                    <Button variant="contained" onClick={saveItem} disabled={saving}>
                        {saving ? "Saving…" : "Save"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={borrowDialog} onClose={() => setBorrowDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Record borrowing</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={12}>
                            <FormControl fullWidth required>
                                <InputLabel>Item</InputLabel>
                                <Select label="Item" value={borrowForm.itemId} onChange={(e) => setBorrowForm({ ...borrowForm, itemId: e.target.value })}>
                                    {availableItems.map((i) => {
                                        const id = i._id || i.id;
                                        return (
                                            <MenuItem key={id} value={id}>
                                                {i.title} ({i.quantityAvailable} avail.)
                                            </MenuItem>
                                        );
                                    })}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Borrower type</InputLabel>
                                <Select
                                    label="Borrower type"
                                    value={borrowForm.borrowerType}
                                    onChange={(e) => setBorrowForm({ ...borrowForm, borrowerType: e.target.value, studentId: "", teacherId: canManageInventory ? "" : teacherId })}
                                >
                                    {BORROWER_TYPES.map((t) => (
                                        <MenuItem key={t} value={t}>{t}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth type="date" label="Due date" InputLabelProps={{ shrink: true }} value={borrowForm.dueDate} onChange={(e) => setBorrowForm({ ...borrowForm, dueDate: e.target.value })} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth size="small" placeholder="Find borrower…" value={borrowerQuery} onChange={(e) => setBorrowerQuery(e.target.value)} sx={{ mb: 1 }} />
                            {borrowForm.borrowerType === "Student" ? (
                                <FormControl fullWidth required>
                                    <InputLabel>Student</InputLabel>
                                    <Select label="Student" value={borrowForm.studentId} onChange={(e) => setBorrowForm({ ...borrowForm, studentId: e.target.value })}>
                                        {borrowers.students.map((s) => {
                                            const id = s._id || s.id;
                                            return (
                                                <MenuItem key={id} value={id}>
                                                    {s.name} ({s.rollNum})
                                                </MenuItem>
                                            );
                                        })}
                                    </Select>
                                </FormControl>
                            ) : (
                                <FormControl fullWidth required>
                                    <InputLabel>Teacher</InputLabel>
                                    <Select
                                        label="Teacher"
                                        value={borrowForm.teacherId}
                                        onChange={(e) => setBorrowForm({ ...borrowForm, teacherId: e.target.value })}
                                        disabled={!canManageInventory}
                                    >
                                        {borrowers.teachers.map((t) => {
                                            const id = t._id || t.id;
                                            return (
                                                <MenuItem key={id} value={id}>
                                                    {t.name}
                                                </MenuItem>
                                            );
                                        })}
                                    </Select>
                                </FormControl>
                            )}
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth label="Notes" value={borrowForm.notes} onChange={(e) => setBorrowForm({ ...borrowForm, notes: e.target.value })} />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setBorrowDialog(false)}>Cancel</Button>
                    <Button variant="contained" onClick={saveBorrow} disabled={saving}>
                        {saving ? "Saving…" : "Record borrow"}
                    </Button>
                </DialogActions>
            </Dialog>

            <ConfirmDeleteDialog
                open={Boolean(deleteTarget)}
                onClose={() => setDeleteTarget(null)}
                onConfirm={confirmDeleteItem}
                title="Remove library item?"
                message={`Delete "${deleteTarget?.title || "this item"}" from inventory? This cannot be undone.`}
            />

            <Popup showPopup={showPopup} setShowPopup={setShowPopup} message={message} severity={popupSeverity} />
        </Box>
    );
};

export default LibraryManagement;
